import { useHead, useSeoMeta } from '@unhead/react';

export const SITE_URL = 'https://gslaviation.com.au';
export const SITE_NAME = 'GSL Aviation';

/** Default share image used when a page doesn't supply its own. */
export const DEFAULT_OG_IMAGE =
    'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/reef-hopper/green-island-scenic-flight-cairns.jpg';

interface SeoProps {
    readonly title: string;
    readonly description: string;
    /** Canonical path, e.g. "/fleet" or "/cairns/reefhopper". Omit only for non-indexable views. */
    readonly path?: string;
    readonly image?: string;
    readonly type?: 'website' | 'article' | 'product';
    /** One or more schema.org JSON-LD objects injected as <script type="application/ld+json">. */
    readonly jsonLd?: Record<string, unknown> | Record<string, unknown>[];
    readonly noindex?: boolean;
}

/**
 * Single source of truth for per-page SEO: title, meta description, canonical,
 * Open Graph, Twitter cards and optional JSON-LD. Built on unhead so tags update
 * on client-side navigation and clean up on unmount.
 */
export default function Seo({ title, description, path, image, type = 'website', jsonLd, noindex }: SeoProps) {
    const canonical = path ? `${SITE_URL}${path}` : undefined;
    const ogImage = image || DEFAULT_OG_IMAGE;

    useSeoMeta({
        title,
        description,
        robots: noindex ? 'noindex, nofollow' : 'index, follow',
        // og:type "product" is valid HTML but isn't in unhead's typed union.
        ogType: type as 'website' | 'article',
        ogSiteName: SITE_NAME,
        ogTitle: title,
        ogDescription: description,
        ogUrl: canonical,
        ogImage,
        twitterCard: 'summary_large_image',
        twitterTitle: title,
        twitterDescription: description,
        twitterImage: ogImage,
    });

    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

    useHead({
        link: canonical ? [{ rel: 'canonical', href: canonical }] : [],
        script: schemas.map((schema) => ({
            type: 'application/ld+json',
            innerHTML: JSON.stringify(schema),
        })),
    });

    return null;
}
